import { IColumn, IDocumentTable, IRow, IValidatorBase, IValidator, ITableValidator } from '@models/Forms/IForms';
import { IDocumentScheme } from '@models/document/IDocumentScheme';

export interface IDependencyEdge {
    key: string;
    types: string[];
}

export interface IDependencyNode {
    fieldKey: string;
    fieldName: string;
    dependsOn: IDependencyEdge[];
    dependedBy: IDependencyEdge[];
}

const FIELD_REF_REGEX = /\{(?:\|Document\|)?([^{}|]+)\}/g;

function extractFieldRefs(formula: string | undefined | null): string[] {
    if (!formula) return [];
    const refs: string[] = [];
    let match: RegExpExecArray | null;
    const regex = new RegExp(FIELD_REF_REGEX.source, 'g');
    while ((match = regex.exec(formula)) !== null) {
        const ref = match[1];
        if (!['isView', 'isEdit', 'isNew', 'now', 'currentYear', 'nowOnlyDate'].includes(ref)) {
            refs.push(ref);
        }
    }
    return refs;
}

type TaggedFormula = { type: string; formula: string };
type FieldEntry = { name: string; formulas: TaggedFormula[] };

function addTagged(result: Map<string, FieldEntry>, key: string | null | undefined, name: string, pairs: [string, string | undefined | null][]) {
    if (!key) return;
    const tagged = pairs.filter((p) => p[1]).map(([type, formula]) => ({ type, formula: formula! }));
    const existing = result.get(key);
    if (existing) {
        existing.formulas.push(...tagged);
    } else {
        result.set(key, { name: name ?? key, formulas: tagged });
    }
}

function collectTableColumns(table: IDocumentTable, result: Map<string, FieldEntry>) {
    for (const col of table.tableColumn ?? []) {
        addTagged(result, col.key, col.name, [['required', col.required], ['visibility', col.visibilityRules], ['readonly', col.readonly], ['min', col.min], ['max', col.max]]);
    }
    for (const col of table.tableColumnDict ?? []) {
        addTagged(result, col.key, col.name, [['required', col.required], ['visibility', col.visibilityRules], ['readonly', col.readonly]]);
    }
    for (const col of table.tableColumnAbook ?? []) {
        addTagged(result, col.key, col.name, [['required', col.required], ['visibility', col.visibilityRules], ['readonly', col.readonly]]);
    }
    for (const col of table.tableColumnCalc ?? []) {
        addTagged(result, col.key, col.name, [['required', col.required], ['visibility', col.visibilityRules], ['readonly', col.readonly], ['formula', col.formula]]);
    }
    for (const col of table.tableColumnAutoComplete ?? []) {
        addTagged(result, col.key, col.name, [['required', col.required], ['visibility', col.visibilityRules], ['readonly', col.readonly]]);
    }
    for (const col of table.tableColumnUrl ?? []) {
        addTagged(result, col.key, col.name, [['visibility', col.visibilityRules]]);
    }
    for (const sub of table.tables ?? []) {
        addTagged(result, sub.key, sub.name, [['visibility', sub.visibilityRules], ['addRowButton', sub.addRowButtonRules], ['editInForm', sub.editInFormFormula]]);
        collectTableColumns(sub, result);
    }
}

function collectFieldsFromColumn(column: IColumn, result: Map<string, FieldEntry>) {
    for (const field of column.fields ?? []) {
        addTagged(result, field.key, field.name, [['required', field.required], ['visibility', field.visibilityRules], ['readonly', field.readonly], ['min', field.min], ['max', field.max]]);
    }
    for (const dict of column.dicts ?? []) {
        addTagged(result, dict.key, dict.name, [['required', dict.required], ['visibility', dict.visibilityRules], ['readonly', dict.readonly]]);
    }
    for (const abook of column.abooks ?? []) {
        addTagged(result, abook.key, abook.name, [['required', abook.required], ['visibility', abook.visibilityRules], ['readonly', abook.readonly]]);
    }
    for (const calc of column.calcs ?? []) {
        addTagged(result, calc.key, calc.name, [['required', calc.required], ['visibility', calc.visibilityRules], ['readonly', calc.readonly], ['formula', calc.formula]]);
    }
    for (const ac of column.autoCompletes ?? []) {
        addTagged(result, ac.key, ac.name, [['required', ac.required], ['visibility', ac.visibilityRules], ['readonly', ac.readonly]]);
    }
    for (const url of column.urls ?? []) {
        addTagged(result, url.key, url.name, [['visibility', url.visibilityRules]]);
    }
    for (const table of column.tables ?? []) {
        addTagged(result, table.key, table.name, [['visibility', table.visibilityRules], ['addRowButton', table.addRowButtonRules], ['editInForm', table.editInFormFormula]]);
        collectTableColumns(table, result);
    }
    for (const row of column.rows ?? []) {
        collectFieldsFromRow(row, result);
    }
    for (const block of column.blocks ?? []) {
        // Псевдоузел графа: у блока нет ключа реквизита, но его формулы зависят от полей
        // (ср. `__validators__` ниже). Префикс исключает совпадение с ключом поля.
        addTagged(result, block.id ? `block:${block.id}` : undefined, `Блок «${block.name}»`, [
            ['visibility', block.visibilityRules],
            ['formula', block.nameFormula],
        ]);
        for (const row of block.rows?.row ?? []) {
            collectFieldsFromRow(row, result);
        }
    }
}

function collectFieldsFromRow(row: IRow, result: Map<string, FieldEntry>) {
    for (const column of row.columns ?? []) {
        collectFieldsFromColumn(column, result);
    }
}

function collectValidatorFormulas(validators: IValidatorBase[] | undefined): TaggedFormula[] {
    if (!validators) return [];
    const formulas: TaggedFormula[] = [];
    for (const v of validators) {
        if (v.type === 'standart') {
            const sv = v as IValidator;
            if (sv.condition) formulas.push({ type: 'validator', formula: sv.condition });
            if (sv.message) formulas.push({ type: 'validator-msg', formula: sv.message });
        } else if (v.type === 'table') {
            const tv = v as ITableValidator;
            if (tv.condition) formulas.push({ type: 'validator', formula: tv.condition });
            if (tv.message) formulas.push({ type: 'validator-msg', formula: tv.message });
            formulas.push(...collectValidatorFormulas(tv.validators));
        }
    }
    return formulas;
}

export function buildDependencyGraph(scheme: IDocumentScheme | undefined): IDependencyNode[] {
    if (!scheme?.form?.view?.rows?.row) return [];

    const fieldsMap = new Map<string, FieldEntry>();

    for (const row of scheme.form.view.rows.row) {
        collectFieldsFromRow(row, fieldsMap);
    }

    const validatorFormulas = collectValidatorFormulas(scheme.form.view.validators?.validator);
    if (validatorFormulas.length > 0) {
        const existing = fieldsMap.get('__validators__');
        if (existing) {
            existing.formulas.push(...validatorFormulas);
        } else {
            fieldsMap.set('__validators__', { name: 'Валидаторы документа', formulas: validatorFormulas });
        }
    }

    // Build a lookup: short name -> full key
    const shortToKey = new Map<string, string>();
    for (const key of fieldsMap.keys()) {
        if (!key) continue;
        const short = key.replace(/^\|Document\|/, '');
        shortToKey.set(short, key);
        shortToKey.set(key, key);
    }

    // Build edges with types
    const nodesMap = new Map<string, { name: string; dependsOn: Map<string, Set<string>>; dependedBy: Map<string, Set<string>> }>();

    for (const [key, { name, formulas }] of fieldsMap) {
        if (!key) continue;
        if (!nodesMap.has(key)) {
            nodesMap.set(key, { name, dependsOn: new Map(), dependedBy: new Map() });
        }
        const node = nodesMap.get(key)!;
        for (const { type, formula } of formulas) {
            for (const ref of extractFieldRefs(formula)) {
                const resolvedKey = shortToKey.get(ref);
                if (resolvedKey && resolvedKey !== key) {
                    // key depends on resolvedKey via type
                    const existing = node.dependsOn.get(resolvedKey);
                    if (existing) {
                        existing.add(type);
                    } else {
                        node.dependsOn.set(resolvedKey, new Set([type]));
                    }
                }
            }
        }
    }

    // Build reverse edges
    for (const [key, node] of nodesMap) {
        for (const [depKey, types] of node.dependsOn) {
            if (!nodesMap.has(depKey)) {
                const depEntry = fieldsMap.get(depKey);
                nodesMap.set(depKey, { name: depEntry?.name ?? depKey, dependsOn: new Map(), dependedBy: new Map() });
            }
            const depNode = nodesMap.get(depKey)!;
            const existing = depNode.dependedBy.get(key);
            if (existing) {
                for (const t of types) existing.add(t);
            } else {
                depNode.dependedBy.set(key, new Set(types));
            }
        }
    }

    // Convert to output
    const result: IDependencyNode[] = [];
    for (const [key, node] of nodesMap) {
        if (node.dependsOn.size === 0 && node.dependedBy.size === 0) continue;
        result.push({
            fieldKey: key,
            fieldName: node.name,
            dependsOn: [...node.dependsOn.entries()].map(([k, types]) => ({ key: k, types: [...types] })),
            dependedBy: [...node.dependedBy.entries()].map(([k, types]) => ({ key: k, types: [...types] })),
        });
    }

    return result.sort((a, b) => b.dependedBy.length - a.dependedBy.length);
}
